import React, { FC, ReactNode, createContext, useContext, useState } from 'react';
import { ISegmentsApiService } from '../services/segments-api-service';
import { AppConfigServiceFactory } from '../factories/app-config-service-factory';
import { IServiceFactory } from '../../common/types/service-factory';
import { SegmentsApiServiceFactory } from '../factories/segments-api-service-factory';
import { IAppConfigService } from '../interfaces/app-config-service';
import { IApiService } from '../interfaces/api-service';
import { useApiServiceFactory } from '../../common/providers/ReactKitProvider';
import { IContactsApiService } from '../services/contacts-api-service';
import { ContactsApiServiceFactory } from '../factories/contacts-api-service-factory';
import { IViewsApiService } from '../services/view-api-service';
import { ViewsApiServiceFactory } from '../factories/views-api-service-factory';
import { IReferenceValuesApiService } from '../services/reference-values-api-service';
import { ReferenceValuesApiServiceFactory } from '../factories/reference-values-api-service-factory';
import { IPowerBIApiService } from 'api/services/powerbi-api-service';
import { PowerBiApiServiceFactory } from 'api/factories/powerbi-api-service-factory';
import { IAuthApiService } from 'api/services/auth-api-service';
import { AuthApiServiceFactory } from 'api/factories/auth-api-service-factory';
import { IActivityApiService } from '../services/activity-api-service';
import { ActivityApiServiceFactory } from '../factories/activity-api-service-factory';
import { IAIChatApiService } from 'api/services/ai-chat-api-service';
import { AIChatApiServiceFactory } from 'api/factories/ai-chat-api-service-factory';
import { FavouriteApiService } from 'api/services/favourite-api-service';
import { FavouriteApiServiceFactory } from 'api/factories/favourite-api-service-factory';
import { ITrackingApiService } from 'api/services/tracking-api-service';
import { TrackingApiServiceFactory } from 'api/factories/tracking-api-service-factory';
import { IGrowthModelApiService } from 'api/services/growth-model-api-service';
import { GrowthModelApiServiceFactory } from 'api/factories/growth-model-api-service-factory';
import { IJourneyExplorerApiService } from 'api/services/journey-explorer-api-service';
import { JourneyExplorerApiServiceFactory } from 'api/factories/journey-explorer-api-service-factory';
import { initializeAxiosInterceptors } from '../interfaces/axios.interceptors';
import { useMsal } from '@azure/msal-react';
import { useEffect } from 'react';

interface IAppServiceContainer {
	createAppConfigService(): IAppConfigService;
	createActivityApiService(): IActivityApiService;
	createContactsApiService(): IContactsApiService;
	createReferenceValuesApiService(): IReferenceValuesApiService;
	createSegmentsApiService(): ISegmentsApiService;
	createViewsApiService(): IViewsApiService;
	createPowerBIApiService(): IPowerBIApiService;
	createAuthApiService(): IAuthApiService;
	createAIChatApiService(): IAIChatApiService;
	createFavouriteApiService(): FavouriteApiService;
	createTrackingApiService(): ITrackingApiService;
	createGrowthModelApiService(): IGrowthModelApiService;
	createJourneyExplorerApiService(): IJourneyExplorerApiService;
}

class AppServiceContainer implements IAppServiceContainer {
	private appConfigServiceFactory: IServiceFactory<IAppConfigService>;
	private activityApiServiceFactory: IServiceFactory<IActivityApiService>;
	private contactsApiServiceFactory: IServiceFactory<IContactsApiService>;
	private referenceValuesApiServiceFactory: IServiceFactory<IReferenceValuesApiService>;
	private segmentsApiServiceFactory: IServiceFactory<ISegmentsApiService>;
	private viewsApiServiceFactory: IServiceFactory<IViewsApiService>;
	private powerBiApiServiceFactory: IServiceFactory<IPowerBIApiService>;
	private authApiServiceFactory: IServiceFactory<IAuthApiService>;
	private aiChatApiServiceFactory: IServiceFactory<IAIChatApiService>;
	private favouriteApiServiceFactory: IServiceFactory<FavouriteApiService>;
	private trackingApiServiceFactory: IServiceFactory<ITrackingApiService>;
	private growthModelApiServiceFactory: IServiceFactory<IGrowthModelApiService>;
	private journeyExplorerApiServiceFactory: IServiceFactory<IJourneyExplorerApiService>;

	constructor(apiServiceFactory: IServiceFactory<IApiService>) {
		this.appConfigServiceFactory = new AppConfigServiceFactory();
		const appConfig = this.appConfigServiceFactory.Create();

		this.activityApiServiceFactory = new ActivityApiServiceFactory(apiServiceFactory, appConfig);
		this.contactsApiServiceFactory = new ContactsApiServiceFactory(apiServiceFactory, appConfig);
		this.referenceValuesApiServiceFactory = new ReferenceValuesApiServiceFactory(apiServiceFactory, appConfig);
		this.segmentsApiServiceFactory = new SegmentsApiServiceFactory(apiServiceFactory, appConfig);
		this.viewsApiServiceFactory = new ViewsApiServiceFactory(apiServiceFactory, appConfig);
		this.powerBiApiServiceFactory = new PowerBiApiServiceFactory(apiServiceFactory, appConfig);
		this.authApiServiceFactory = new AuthApiServiceFactory(apiServiceFactory, appConfig);
		this.aiChatApiServiceFactory = new AIChatApiServiceFactory(apiServiceFactory, appConfig);
		this.favouriteApiServiceFactory = new FavouriteApiServiceFactory(apiServiceFactory, appConfig);
		this.trackingApiServiceFactory = new TrackingApiServiceFactory(apiServiceFactory, appConfig);
		this.growthModelApiServiceFactory = new GrowthModelApiServiceFactory(apiServiceFactory, appConfig);
		this.journeyExplorerApiServiceFactory = new JourneyExplorerApiServiceFactory(apiServiceFactory, appConfig);
	}

	public createAppConfigService(): IAppConfigService {
		return this.appConfigServiceFactory.Create();
	}

	public createActivityApiService(): IActivityApiService {
		return this.activityApiServiceFactory.Create();
	}

	public createContactsApiService(): IContactsApiService {
		return this.contactsApiServiceFactory.Create();
	}

	public createReferenceValuesApiService(): IReferenceValuesApiService {
		return this.referenceValuesApiServiceFactory.Create();
	}

	public createSegmentsApiService(): ISegmentsApiService {
		return this.segmentsApiServiceFactory.Create();
	}

	public createViewsApiService(): IViewsApiService {
		return this.viewsApiServiceFactory.Create();
	}

	public createPowerBIApiService(): IPowerBIApiService {
		return this.powerBiApiServiceFactory.Create();
	}

	public createAuthApiService(): IAuthApiService {
		return this.authApiServiceFactory.Create();
	}

	public createAIChatApiService(): IAIChatApiService {
		return this.aiChatApiServiceFactory.Create();
	}

	public createFavouriteApiService(): FavouriteApiService {
		return this.favouriteApiServiceFactory.Create();
	}

	public createTrackingApiService(): ITrackingApiService {
		return this.trackingApiServiceFactory.Create();
	}

	public createGrowthModelApiService(): IGrowthModelApiService {
		return this.growthModelApiServiceFactory.Create();
	}

	public createJourneyExplorerApiService(): IJourneyExplorerApiService {
		return this.journeyExplorerApiServiceFactory.Create();
	}
}

const AppServiceContext: React.Context<IAppServiceContainer> = createContext(null as unknown as IAppServiceContainer);

interface IAppServiceProviderProps {
	children?: ReactNode;
}

export const AppServiceProvider: FC<IAppServiceProviderProps> = ({ children }) => {
	const [appServiceContainer] = useState(new AppServiceContainer(useApiServiceFactory()));
	const { instance } = useMsal();

	// Comprehensive cleanup for existing users with old localStorage data
	useEffect(() => {
		try {
			// Simple cleanup - just clear any problematic MSAL telemetry keys
			// The main cleanup will happen when token acquisition fails
			const msalTelemetryKeys = [
				'server-telemetry-*', // Pattern for any server-telemetry keys
				'msal.error.description',
				'msal.error',
				'msal.nonce.idtoken',
				'msal.nonce.client_info',
				'msal.client.info',
				'msal.session.state',
				'msal.error.status',
				'msal.error.timestamp'
			];
			
			// Clear problematic MSAL telemetry keys
			msalTelemetryKeys.forEach(key => {
				if (key.includes('*')) {
					// Handle pattern matching for server-telemetry keys
					const pattern = key.replace('*', '');
					Object.keys(localStorage).forEach(storageKey => {
						if (storageKey.includes(pattern)) {
							localStorage.removeItem(storageKey);
							console.log(`🧹 Cleared MSAL telemetry key: ${storageKey}`);
						}
					});
				} else {
					if (localStorage.getItem(key)) {
						localStorage.removeItem(key);
						console.log(`🧹 Cleared MSAL key: ${key}`);
					}
				}
			});
			
			// Check for any server-telemetry keys with failed requests
			Object.keys(localStorage).forEach(key => {
				if (key.startsWith('server-telemetry-')) {
					try {
						const telemetryData = JSON.parse(localStorage.getItem(key) || '{}');
						if (telemetryData.failedRequests && telemetryData.failedRequests.length > 5) {
							console.log(`🚨 Detected problematic telemetry key with ${telemetryData.failedRequests.length} failed requests: ${key}`);
							localStorage.removeItem(key);
						}
					} catch (e) {
						// If parsing fails, remove the key anyway
						localStorage.removeItem(key);
					}
				}
			});
			
			console.log('🧹 Cleared problematic MSAL keys for existing user');
		} catch (error) {
			console.log('Error clearing custom storage:', error);
		}
	}, []);

	useEffect(() => {
		if (instance) {
			// Small delay to ensure MSAL is fully initialized
			const timer = setTimeout(() => {
				console.log('🔧 MSAL instance ready, initializing interceptors...');
				initializeAxiosInterceptors(instance);
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [instance]);

	return <AppServiceContext.Provider value={appServiceContainer}>{children}</AppServiceContext.Provider>;
};

export const useConfiguration = (): IAppConfigService => {
	return useContext(AppServiceContext).createAppConfigService();
};

export const useActivityApiService = (): IActivityApiService => {
	return useContext(AppServiceContext).createActivityApiService();
};

export const useContactsApiService = (): IContactsApiService => {
	return useContext(AppServiceContext).createContactsApiService();
};

export const useReferenceValuesApiService = (): IReferenceValuesApiService => {
	return useContext(AppServiceContext).createReferenceValuesApiService();
};

export const useSegmentsApiService = (): ISegmentsApiService => {
	return useContext(AppServiceContext).createSegmentsApiService();
};

export const useViewsApiService = (): IViewsApiService => {
	return useContext(AppServiceContext).createViewsApiService();
};

export const usePowerBIApiService = (): IPowerBIApiService => {
	return useContext(AppServiceContext).createPowerBIApiService();
};

export const useAuthApiService = (): IAuthApiService => {
	return useContext(AppServiceContext).createAuthApiService();
};

export const useAiChatApiService = (): IAIChatApiService => {
	return useContext(AppServiceContext).createAIChatApiService();
};

export const useFavouriteApiService = (): FavouriteApiService => {
	return useContext(AppServiceContext).createFavouriteApiService();
};

export const useTrackingApiService = (): ITrackingApiService => {
	return useContext(AppServiceContext).createTrackingApiService();
};

export const useGrowthModelApiService = (): IGrowthModelApiService => {
	return useContext(AppServiceContext).createGrowthModelApiService();
};

export const useJourneyExplorerApiService = (): IJourneyExplorerApiService => {
	return useContext(AppServiceContext).createJourneyExplorerApiService();
};
